import {DataEditorProps, DataEditorRef, GridCell, Item, Theme} from "@glideapps/glide-data-grid";
import {forwardRef, useCallback, useMemo} from "react";
import {useMantineTheme} from "@mantine/core";
import {DataGrid} from "./DataGrid";
import {getDataGridTheme, useOtsColorScheme, useOtsBrand} from "./dataGridTheme";

export type ThemedDataGridProps = DataEditorProps & {colorScheme?: "light" | "dark"};

/** Text for a cell or row that keeps its own light background (a status color) on the dark grid. */
const ON_LIGHT_OVERRIDE: Partial<Theme> = {
  textDark: "#111827",
  textMedium: "#374151",
  textLight: "#4b5563",
};

const readableOnOwnBackground = (override: Partial<Theme> | undefined): Partial<Theme> | undefined =>
  override?.bgCell && !override.textDark ? {...ON_LIGHT_OVERRIDE, ...override} : override;

export const ThemedDataGrid = forwardRef<DataEditorRef, ThemedDataGridProps>((props, ref) => {
  const {theme, getCellContent, getRowThemeOverride, colorScheme, ...rest} = props;

  const appTheme = useMantineTheme();
  const inheritedScheme = useOtsColorScheme();
  const brand = useOtsBrand();
  const scheme = colorScheme ?? inheritedScheme ?? appTheme.colorScheme;
  const dark = scheme === "dark";

  const dataGridTheme = useMemo(
    () => ({
      fontFamily: appTheme.fontFamily,
      accentColor: appTheme.fn.primaryColor(),
      ...getDataGridTheme(scheme, brand),
      ...theme,
    }),
    [theme, appTheme, scheme, brand]
  );

  // Status colors on cells and rows were chosen for dark text; keep them readable when the grid is dark.
  const getDarkCellContent = useCallback(
    (cell: Item): GridCell => {
      const content = getCellContent(cell);
      const override = readableOnOwnBackground(content.themeOverride);
      return override === content.themeOverride ? content : {...content, themeOverride: override};
    },
    [getCellContent]
  );

  const getDarkRowThemeOverride = useMemo<DataEditorProps["getRowThemeOverride"]>(
    () => getRowThemeOverride && ((row) => readableOnOwnBackground(getRowThemeOverride(row))),
    [getRowThemeOverride]
  );

  return (
    <DataGrid
      {...rest}
      getCellContent={dark ? getDarkCellContent : getCellContent}
      getRowThemeOverride={dark ? getDarkRowThemeOverride : getRowThemeOverride}
      theme={dataGridTheme}
      ref={ref}
    />
  );
});
